import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { getBudgets, approveBudget, downloadBudgetPdf } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, DollarSign, Download } from 'lucide-react';

const Approvals = ({ user }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadBudgets();
  }, []);

  const loadBudgets = async () => {
    try {
      const response = await getBudgets({ status: 'pending' });
      setBudgets(response.data);
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (budgetId) => {
    setActionLoading(true);
    try {
      await approveBudget(budgetId, { approved: true });
      toast.success('Budget approved successfully!');
      loadBudgets();
      setSelectedBudget(null);
    } catch (error) {
      toast.error('Failed to approve budget');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (budgetId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await approveBudget(budgetId, { approved: false, rejection_reason: rejectionReason });
      toast.success('Budget rejected');
      loadBudgets();
      setSelectedBudget(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Failed to reject budget');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async (budget) => {
    try {
      const response = await downloadBudgetPdf(budget.budget_id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Budget_${budget.event_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators can access this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Budget Approvals</h2>
            <p className="text-sm text-muted-foreground">Review and approve budget requests</p>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : budgets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <CheckCircle2 className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">All caught up!</h3>
              <p className="text-muted-foreground">No pending budget approvals</p>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto space-y-4">
              {budgets.map((budget) => (
                <Card key={budget.budget_id} className="card-hover" data-testid={`budget-approval-${budget.budget_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between space-y-4 md:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-bold text-foreground">{budget.event_name}</h3>
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                            Pending
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3">Submitted by {budget.submitted_by_name}</p>
                        
                        <div className="mb-4">
                          <p className="text-2xl font-bold text-primary">${budget.total_amount.toFixed(2)}</p>
                        </div>
                        
                        <div className="space-y-2">
                          <p className="font-semibold text-sm text-foreground">Categories:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {budget.categories.map((cat, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-accent/30 rounded-lg">
                                <span className="text-sm text-foreground">{cat.name}</span>
                                <span className="text-sm font-semibold text-foreground">${cat.amount.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {budget.notes && (
                          <div className="mt-4">
                            <p className="text-sm font-semibold text-foreground mb-1">Notes:</p>
                            <p className="text-sm text-muted-foreground">{budget.notes}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex md:flex-col space-x-3 md:space-x-0 md:space-y-2">
                        <Button
                          onClick={() => handleDownloadPdf(budget)}
                          variant="outline"
                          size="sm"
                          data-testid={`download-budget-${budget.budget_id}`}
                          className="md:w-32"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                        <Button
                          onClick={() => handleApprove(budget.budget_id)}
                          disabled={actionLoading}
                          data-testid={`approve-budget-${budget.budget_id}`}
                          className="flex-1 md:w-32 bg-emerald-600 hover:bg-emerald-700"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => setSelectedBudget(budget)}
                          disabled={actionLoading}
                          data-testid={`reject-budget-${budget.budget_id}`}
                          variant="destructive"
                          className="flex-1 md:w-32"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>

      {/* Rejection Dialog */}
      <Dialog open={selectedBudget !== null} onOpenChange={() => { setSelectedBudget(null); setRejectionReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Budget</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-3">Please provide a reason for rejecting this budget:</p>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              data-testid="rejection-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedBudget(null); setRejectionReason(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReject(selectedBudget.budget_id)}
              disabled={actionLoading}
              data-testid="confirm-reject-button"
            >
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Approvals;
